import { useState, useEffect, useCallback } from "react";
import { useOIDCContext } from ".";
import { UseRequestReturn, OIDCConfig, ApiError } from "../types";

// Timeout and retry configuration
const REQUEST_TIMEOUT = 120000; // 120 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay

function redirectToLogin(oidcConfig: OIDCConfig): void {
  const { clientId, authUrl, redirectUrl } = oidcConfig;

  window.location.href = [
    authUrl,
    "?response_type=code",
    "&scope=openid+profile",
    "&client_id=" + clientId,
    "&redirect_uri=" + redirectUrl,
  ].join("");
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createTimeoutPromise(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      const error = new Error("Request timeout") as ApiError;
      error.statusCode = 408;
      reject(error);
    }, ms);
  });
}

const useRequest = <T = unknown>(): UseRequestReturn<T> => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<T | null>(null);
  const [isError, setIsError] = useState<Error | null>(null);
  const { oidcConfig } = useOIDCContext();

  const apiRequest = useCallback(
    async (path: string, opts: RequestInit = {}): Promise<Response | Error> => {
      setIsLoading(true);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const fetchPromise = fetch(path, {
            ...opts,
            signal: controller.signal,
          });

          const res = await Promise.race([fetchPromise, createTimeoutPromise(REQUEST_TIMEOUT)]);
          clearTimeout(timeoutId);
          setIsLoading(false);

          if (res.ok) {
            return Promise.resolve(res);
          }

          const err = new Error(res.statusText) as ApiError;
          err.statusCode = res.status || 500;

          // Retry on specific HTTP status codes
          if ([429, 500, 502, 503, 504].includes(err.statusCode) && attempt < MAX_RETRIES) {
            lastError = err;
            const delay = RETRY_DELAY * Math.pow(2, attempt);
            await sleep(delay);
            continue;
          }

          return Promise.resolve(err);
        } catch (error: any) {
          // Network error or timeout
          lastError = error as Error;

          if (attempt < MAX_RETRIES) {
            // Retry on network errors (ECONNRESET, ETIMEDOUT, etc.)
            if (
              error?.name === "AbortError" ||
              error?.code === "ECONNRESET" ||
              error?.code === "ETIMEDOUT"
            ) {
              const delay = RETRY_DELAY * Math.pow(2, attempt);
              await sleep(delay);
              continue;
            }
          }

          clearTimeout(timeoutId);
          setIsLoading(false);
          return Promise.resolve(error as Error);
        }
      }

      clearTimeout(timeoutId);
      setIsLoading(false);
      return Promise.resolve(lastError || new Error("Request failed"));
    },
    []
  );

  const load = useCallback(
    async (...args: [string, RequestInit?]): Promise<T | null> => {
      if (!oidcConfig) return null;

      try {
        const res = await apiRequest(...args);
        const statusCode = (res as Response).status || (res as ApiError).statusCode;

        switch (statusCode) {
          case 401:
            redirectToLogin(oidcConfig);
            return null;
          case 200:
          case 304: {
            const jsonData = await (res as Response).json();
            setData(jsonData);
            setIsError(null);
            return Promise.resolve(jsonData);
          }
          default:
            setIsError(res as Error);
            console.error("unexpected response", res);
            return null;
        }
      } catch (error) {
        console.error("fetch error", error);
        return Promise.reject(null);
      }
    },
    [oidcConfig, apiRequest]
  );

  useEffect(() => {
    return () => {
      setIsLoading(false);
      setData(null);
      setIsError(null);
    };
  }, []);

  return {
    data,
    isLoading,
    isError,
    load,
    apiRequest,
  };
};

export default useRequest;
